import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useRouter } from 'next/router';
import {
	faShieldAlt,
	faAt,
	faArrowRight,
} from '@fortawesome/pro-light-svg-icons';
import validateEmailAddress from 'pruve/dist/validators/email';
import Link from 'next/link';
import Button from '../components/Button/Button';
import { Input } from '../components/Input/Input';
import LoadingOverlay from '../components/LoadingOverlay/LoadingOverlay';
import { useLoginMutation } from '../lib/api/hooks/mutations/Auth/useLoginMutation';
import { useStore } from '../lib/store/store';
import useScrollToError from '../lib/utils/hooks/useScrollToError';
import useDevice from '../lib/utils/hooks/useDevice';

export default function Login() {
	const { handleSubmit, control, errors } = useForm();
	const [loading, setLoading] = useState(false);
	const { push } = useRouter();
	const redirectPath = useStore(state => state.redirectPath);
	useScrollToError(errors);

	const loginMutation = useLoginMutation({
		onSuccess: () => {
			const path = redirectPath || '/dashboard';
			push(path);
		},
		onSettled: () => {
			setLoading(false);
		},
	});

	const onSubmit = ({
		email,
		password,
	}: {
		email: string;
		password: string;
	}) => {
		setLoading(true);
		loginMutation.mutate({ email: email.trim(), password: password.trim() });
	};

	const { isMobile } = useDevice();

	return (
		<div className='w-screen h-screen overflow-hidden flex'>
			{loading && <LoadingOverlay />}
			{isMobile === true && (
				<a
					href='https://www.stryve.online/b-corporation'
					target='_blank'
					rel='noreferrer'
				>
					<img
						className='absolute top-2 right-2'
						src='/images/BCorpBlack.png'
						height='150'
						width='70'
						alt='bcorp logo'
					/>
				</a>
			)}
			<div className='overflow-x-hidden flex w-full h-full xl:min-w-[60vw] flex-col flex-1 items-center justify-center p-6 bg-grey-800 shadow-lg'>
				<div className='flex flex-col items-start mx-12 w-full h-full max-w-2xl justify-center'>
					<form
						onSubmit={handleSubmit(onSubmit)}
						className='w-full max-w-md mx-auto'
					>
						<img
							className='w-auto h-10 mb-4'
							src='/images/logo-black.svg'
							alt="Stryve's Logo"
						/>
						<h1 className='text-3xl'>Welcome back </h1>
						<p className='text-lg font-light mb-8'>
							Hiring's hard. We've made it easy.
						</p>
						{loginMutation.isError && (
							<p className='flex items-center text-red-500 font-xs font-semibold mb-4'>
								Incorrect email address or password. Please try again.
							</p>
						)}
						<Controller
							name='email'
							control={control}
							rules={{
								required: 'Please enter a valid email address',
								validate: value => {
									return (
										validateEmailAddress(value) ||
										'Please enter a valid email address'
									);
								},
							}}
							className=''
							defaultValue=''
							render={props => {
								return (
									<Input
										type='text'
										onChange={value => {
											props.onChange(value);
										}}
										nullable={false}
										error={errors?.email?.message}
										icon={faAt}
										label='Email address'
										value={props.value}
										name='email'
										placeholder='Please enter your email address'
										autoComplete
									/>
								);
							}}
						/>
						<div className='my-6' />
						<Controller
							name='password'
							control={control}
							rules={{
								required: 'Please enter your password',
							}}
							defaultValue=''
							render={props => {
								return (
									<Input
										type='password'
										onChange={value => {
											props.onChange(value);
										}}
										value={props.value}
										error={errors?.password?.message}
										icon={faShieldAlt}
										label='Password'
										name='password'
										placeholder='Please enter your password'
										nullable={false}
									/>
								);
							}}
						/>
						<Link href='/forgot-password'>
							<a>
								<p className='text-right text-base pt-2 hover:underline'>
									Forgot your password?
								</p>
							</a>
						</Link>
						<Button
							type='submit'
							proceed={true}
							onClick={() => {}}
							ariaLabel='Sign in'
							icon={faArrowRight}
						>
							Sign in
						</Button>
						<div className='text-slate-900 w-full text-lg font-light text-center block mt-8 '>
							<div className='inline-block'>
								<p className='inline'>Don't have an account?</p>
								<Link href='/register'>
									<a className='ml-1 font-semibold underline inline'>
										Sign up!
									</a>
								</Link>
							</div>
							<p className='font-light text-green-900 text-lg mb-2 text-center'>
								14 day free trial. No card required.
							</p>
						</div>
					</form>
				</div>
			</div>
			{isMobile === false && (
				<div
					className='lg:block relative flex-1 h-screen bg-pink-900 w-full'
					style={{
						backgroundImage: `url('/images/backgrounds/loginBackground.svg')`,
						objectFit: 'fill',
						backgroundSize: 'cover',
					}}
				>
					<a
						href='https://www.stryve.online/b-corporation'
						target='_blank'
						rel='noreferrer'
					>
						<img
							className='absolute top-2 right-2'
							src='/images/BCorpWhite.png'
							height='150'
							width='70'
							alt='bcorp logo'
						/>
					</a>
				</div>
			)}
		</div>
	);
}
